<script setup lang="ts">
/**
 * The content slide-over: an article, trade alert or webinar over the page it was opened from. Mounted once, in
 * App.vue, outside <router-view>, so it outlives page changes; the address drives it (UX roadmap #14): ?preview= opens
 * it, Back closes it, and a link inside it to another page closes it.
 */
import { computed, shallowRef, watch } from "vue";
import { useWindowSize } from "@vueuse/core";

import ContentSkeleton from "@/components/layout/ContentSkeleton.vue";
import pinia from "@/plugins/PiniaPlugin";
import { useApplicationStore } from "@/stores/Application";
import { useCloseOverlaysOnNavigation } from "@/composables/useCloseOverlaysOnNavigation";
import { useContentPreviewRoute } from "@/composables/useContentPreviewRoute";
import { lazyView } from "@/utils/lazy-view";

import type { ContentfulItem } from "@tmi/types/entities";

// Renders only inside the open Drawer.
const ContentModal = lazyView( () => import( "@/components/modals/ContentModal.vue" ) );

useCloseOverlaysOnNavigation();
const { drawerVisible, isResolving } = useContentPreviewRoute();

const appStore = useApplicationStore( pinia );
const hasContent = computed< boolean >( () => !!appStore.contentItem );
// ContentModal reads the item once, in setup: a second item opened over the first gets a fresh one. The last key is
// kept so the item stays on screen while the drawer slides out, rather than an empty panel.
const modalKey = shallowRef< string >( '' );
watch( () => ( appStore.contentItem as ContentfulItem | undefined )?.contentfulId, ( id: string | undefined ) => {
  if ( id ) modalKey.value = `${ appStore.contentKey }:${ id }`;
}, { immediate: true } );
const showModal = computed< boolean >( () => hasContent.value || ( !drawerVisible.value && !!modalKey.value ) );

const { width } = useWindowSize();
const smallScreenSize = computed< boolean >( () => width.value < 768 );
</script>

<template>
  <!-- Widths are !important because PrimeVue's unlayered `.p-drawer-right .p-drawer { width: 20rem }` outranks utilities.
       The width is the article column: .body-content's 72ch measure at prose-xl's 20px is 90ch at the drawer's 16px,
       plus the content's 1.25rem padding each side, so the title, byline and tickers line up with the text below them
       instead of spanning 85% of a wide screen over a narrow centred column. Never wider than 85% of the window.
       No @hide: every way of closing it (the X, Esc, the mask) goes through v-model, and a late hide from the leave
       transition would close an item opened straight after. -->
  <Drawer v-model:visible="drawerVisible" :position="smallScreenSize ? 'full' : 'right'" :block-scroll="true" class="w-full! md:w-[min(85%,calc(90ch_+_2.5rem))]!">
    <ContentModal v-if="showModal" :key="modalKey" />
    <ContentSkeleton v-else-if="isResolving" label="Loading preview…" />
  </Drawer>
</template>
